/**
 * External Dependencies
 */
import { flushSync } from 'react-dom';
import { debounce, has } from 'lodash';
import * as yup from 'yup';
import React, { useEffect, useState, useMemo } from 'react';
import { usePage } from '@inertiajs/react';

/**
 * Internal Dependencies
 */
import Button from '@/common/components/button/button';
import InputStays from '@/common/components/input-stays/input-stays';
import ValidationErrors from '@/common/components/errors/validation-errors';
import AddPropertiesList from '@/hosts-marketplace/blocks/add-properties/add-properties-list';
import FormRegisterStaysReviewProperties from '@/hosts-marketplace/blocks/forms/form-register-stays/form-register-stays-review-properties';
import { generateId } from '@/common/helpers/helpers';
import { getAutocompleteSuggestions, getPlaceDetails } from '@/services/google-places/google-places';
import LogoAlt from '@/assets/images/logo-alt.svg';
import { usePropertyLocationContext } from '@/common/contexts/use-property-location';

const formSchema = yup.object().shape({
    airbnb_url: yup.string().required('Please enter website'),
    airbnb_address: yup.string().required('Please enter valid Address'),
    address_2: yup.string().max(255, 'The field text is too long'),
});

const defaultFieldValues = {
    airbnb_url: '',
    airbnb_address: '',
    place_id: '',
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip: '',
    lat: '',
    lng: '',
};

const FormRegisterStays = ({
    errors,
    data,
    setData,
    hostProperties,
    hideHeader = false,
    hideFooter = false,
    onSubmit = () => {},
    onHostPropertyUpdate = () => {},
    onHostPropertyDelete = () => {},
    propertiesList = [],
}) => {
    const [formFields, setFormFields] = useState(defaultFieldValues);
    const [formFieldsErrors, setFormFieldsErrors] = useState({});
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [predictions, setPredictions] = useState([]);
    const { updateMapToUserLocation, handleGeoCordinatesChange } = usePropertyLocationContext();
    const hasErrors = useMemo(() => Object.keys(formFieldsErrors).length !== 0, [formFieldsErrors]);
    const [error, setError] = useState(null);

    const { externalLinks } = usePage().props;
    const supportEmail = import.meta.env.VITE_INHOUSE_CONTACT_EMAIL;

    const debounceGetPredictions = useMemo(
        () =>
            debounce((value) => {
                if (!value) return setPredictions([]);

                (async () => {
                    const predictions = await getAutocompleteSuggestions(value);

                    if (predictions.length) setPredictions(predictions);
                })();
            }, 350),
        []
    );

    const propertiesForReviewCount = useMemo(() => {
        const hostPropertiesCount = hostProperties?.filter((i) => i.verified === 0).length || 0;
        const dataCount = data.length || 0;

        return hostPropertiesCount + dataCount;
    }, [hostProperties, data]);

    // On Field Change
    const onHandleChange = (event) => {
        setFormFields((prevValue) => ({
            ...prevValue,
            [event.target.name]: event.target.value,
        }));
    };

    // On suggestion Click
    const onAddressSuggestionClick = async (place) => {
        let placeDetails;

        try {
            const placeData = await getPlaceDetails(place);

            placeDetails = placeData;
        } catch (e) {
            placeDetails = {
                place_id: place.place_id,
                airbnb_address: place.description,
            };
        }

        handleGeoCordinatesChange(placeDetails);
        setPredictions([]);
        setFormFields((prevValue) => ({
            ...prevValue,
            ...placeDetails,
        }));
    };

    // Add Property
    const handlePropertyAdd = () => {
        if (hasErrors) {
            setError('Please add a property to sign up.');
            return;
        }

        setError(null); // Clear the error message if hasErrors is false

        setData((prevValue) => [
            {
                ...formFields,
                place_id: formFields.place_id || generateId(),
            },
            ...prevValue,
        ]);
        setFormFields(defaultFieldValues);
        updateMapToUserLocation();
    };

    // Update Property
    const handlePropertyUpdate = async () => {
        if (hasErrors) return;

        if (selectedProperty?.verified !== undefined) {
            await onHostPropertyUpdate(selectedProperty.place_id, formFields);

            setSelectedProperty(null);
            updateMapToUserLocation();
            setFormFields(defaultFieldValues);
            return;
        }

        flushSync(() => {
            setData((prevValue) =>
                prevValue.map((el) =>
                    el?.place_id === selectedProperty.place_id
                        ? {
                              ...formFields,
                          }
                        : el
                )
            );
            setSelectedProperty(null);
            setFormFields(defaultFieldValues);
            updateMapToUserLocation();
        });
    };

    // Edit Property
    const handlePropertyEdit = (property) => {
        if (selectedProperty?.place_id === property.place_id) return;
        const isEmpty = !(formFields.airbnb_url || formFields.airbnb_address);

        const confirm =
            !isEmpty &&
            !window.confirm(
                'Your input is not saved yet. Editing a saved one will result in losing your current input. Are you sure you would like to proceed editing an existing property?'
            );
        if (confirm) return;
        setSelectedProperty({ ...property });
        setFormFields({ ...property });
    };

    // Remove Property
    const handlePropertyRemove = (id) => {
        setData((prevValue) => {
            const updatedProperties = prevValue.filter((item) => item.place_id !== id);
            if (selectedProperty?.place_id) setFormFields(defaultFieldValues);
            setSelectedProperty(null);
            return updatedProperties;
        });
    };

    const onSubmitHandler = (e) => {
        e.preventDefault();
        let parseData = [...data];

        onSubmit(parseData);
    };

    // Get predictions when address changes
    useEffect(() => {
        debounceGetPredictions(formFields.airbnb_address);
    }, [formFields.airbnb_address]);

    // Validate on Form input change
    useEffect(() => {
        const validateForm = async () => {
            // Check the schema if form is valid:
            setFormFieldsErrors([]);

            const isFormValid = await formSchema.isValid(formFields, {
                abortEarly: false,
            });

            if (!isFormValid) {
                formSchema.validate(formFields, { abortEarly: false }).catch((err) => {
                    // Collect all errors in { fieldName: message } format:
                    const errors = err.inner.reduce((acc, error) => {
                        return {
                            ...acc,
                            [error.path]: error.message,
                        };
                    }, {});

                    setFormFieldsErrors(errors);
                });
            }
        };

        validateForm();
    }, [formFields]);

    return (
        <form onSubmit={onSubmitHandler} className="form-guest form-guest--stays">
            {!hideHeader && (
                <>
                    <div className="guest__step">
                        <p>Step 2 of 2</p>
                    </div>
                    <div className="form-guest__image">
                        <a href={externalLinks?.inhouse?.main}>
                            <img src={LogoAlt} alt="" />
                        </a>
                    </div>

                    <div className="form-guest__head">
                        <h1 className="h1 form-guest__title">Add Properties</h1>

                        <p className="color-dark-60 p1">
                            Please enter a URL and address for each of your properties to complete sign up.
                        </p>
                    </div>
                </>
            )}

            <AddPropertiesList
                properties={hostProperties}
                activePropertyId={selectedProperty?.place_id || null}
                onPropertyDelete={onHostPropertyDelete}
            />

            <ValidationErrors errors={errors} />

            <div className="form-guest__body">
                <div>
                    <InputStays
                        id="airbnb_url"
                        name="airbnb_url"
                        label="Property URL (Airbnb, Vrbo, etc)"
                        className="field--floating"
                        isValid={!formFieldsErrors?.airbnb_url}
                        value={formFields.airbnb_url}
                        handleChange={onHandleChange}
                    />
                </div>

                <div className="mt-2">
                    <InputStays
                        name="airbnb_address"
                        id="airbnb_address"
                        className="field--floating"
                        validation="cannot-be-empty"
                        label="Property Address"
                        value={formFields.airbnb_address}
                        isValid={!formFieldsErrors.airbnb_address}
                        suggestions={predictions}
                        suggestionsIcon="location"
                        handleChange={onHandleChange}
                        onSuggestionClick={onAddressSuggestionClick}
                    />
                </div>

                <div className="mt-2">
                    <InputStays
                        id="address_2"
                        name="address_2"
                        label="Unit/Apartment, Suite, etc (optional)"
                        className="field--floating"
                        value={formFields.address_2}
                        isValid={!formFieldsErrors.address_2}
                        handleChange={onHandleChange}
                    />
                </div>
            </div>

            <div className="form-guest__add-property">
                {selectedProperty ? (
                    <button className="btn btn--theme btn--full-w" type="button" onClick={handlePropertyUpdate}>
                        + Update property
                    </button>
                ) : (
                    <button className="btn btn--theme btn--full-w" type="button" onClick={handlePropertyAdd}>
                        {propertiesForReviewCount > 0 ? '+ Add Another Property' : '+ Add Property'}
                    </button>
                )}
            </div>

            <div className="form-guest__actions">
                <div>
                    <Button
                        className="btn--red-dark btn--full-w"
                        type="submit"
                        disabled={propertiesForReviewCount === 0}
                    >
                        Submit
                    </Button>
                </div>
            </div>

            {error && (
                <div className="form-guest__error">
                    <p className="p1 color-error">{error}</p>
                </div>
            )}

            <FormRegisterStaysReviewProperties
                propertiesForReview={propertiesForReviewCount}
                properties={data}
                activePropertyId={selectedProperty?.place_id || null}
                onPropertyEdit={handlePropertyEdit}
                onPropertyRemove={handlePropertyRemove}
            />

            {!hideFooter && (
                <div className="form-guest__bottom">
                    <h3 className="h3 mb-2">Not a host?</h3>

                    <p className="p1 color-dark-60">
                        Interior designers, managers, realtors, setting up a new property, etc.
                    </p>

                    <Button className="btn--full-w btn btn--lightgrey" type="button" onClick={onSubmitHandler}>
                        Request Access
                    </Button>

                    <p className="mb-2 color-mid-gray" style={{ fontSize: '12px', paddingTop: '1.6rem' }}>
                        Questions? We're here to help. <br />
                        <a
                            href={`mailto:mailto:${supportEmail}`}
                            className="link-underlined color-dark-80"
                            style={{ fontSize: '12px' }}
                        >
                            Contact Us
                        </a>
                    </p>
                </div>
            )}
        </form>
    );
};

export default FormRegisterStays;
